import React, { useState } from 'react';
import { Mail, X, RefreshCw, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { getErrorMessage } from '../lib/apiClient';

/**
 * Banner component to prompt users to verify their email address
 * Shows a dismissible banner with option to resend verification email
 */
export default function EmailVerificationBanner({ user, onDismiss }) {
  const [resending, setResending] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Don't show banner if user is verified or banner is dismissed
  if (!user || user.email_verified || dismissed) {
    return null;
  }

  const handleResend = async () => {
    setResending(true);
    try {
      await api.post('/api/auth/resend-verification/', {});
      toast.success('Verificatie-e-mail verzonden! Controleer je inbox.');
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setResending(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <div className="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800">
      <div className="container-padded py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0">
              <Mail className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="text-sm text-amber-800 dark:text-amber-200">
              <span className="font-medium">Verifieer je e-mailadres</span>
              <span className="hidden sm:inline"> - Controleer je inbox voor de verificatielink.</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleResend}
              disabled={resending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                         text-amber-700 dark:text-amber-300
                         bg-amber-100 dark:bg-amber-900/50
                         hover:bg-amber-200 dark:hover:bg-amber-900
                         rounded-md transition-colors"
            >
              {resending ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span className="sr-only sm:not-sr-only sm:inline">Verzenden...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  <span className="sr-only sm:not-sr-only sm:inline">Opnieuw versturen</span>
                </>
              )}
            </button>
            <button
              onClick={handleDismiss}
              className="p-1.5 text-amber-600 dark:text-amber-400
                         hover:text-amber-800 dark:hover:text-amber-200
                         hover:bg-amber-100 dark:hover:bg-amber-900/50
                         rounded-md transition-colors"
              aria-label="Banner sluiten"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
